import { useProductFilters } from "../../hooks/useProductFilters";
import "./Filtres.css";
import { useEffect } from "react";

interface FiltersProps {
  filters: ReturnType<typeof useProductFilters>;
  rooms?: any[];
  selectedSection?: string | null;
}

export default function Filtres({ filters, rooms = [], selectedSection = null }: FiltersProps) {
  const {
    openFilter,
    sortType,
    priceFrom,
    priceTo,
    searchCategory,
    searchRoom,
    selectedFilters,
    searchSection,
    uniqueCategories,
    uniqueSections,
    uniqueRooms,
    setSortType,
    setPriceFrom,
    setPriceTo,
    setSearchCategory,
    setSearchRoom,
    setSelectedFilters,
    setSearchSection,
    toggleFilter,
    resetFilters,
  } = filters;

  useEffect(() => {
    console.log("🎯 FILTRES получил комнаты:", {
      count: rooms.length,
      firstRoom: rooms[0],
      hasSection: rooms[0]?.section,
      allSections: [...new Set(rooms.map(r => r.section))]
    });
  }, [rooms]);

  const normalizeSection = (section: string) => {
    if (!section) return "";
    
    const sectionMap: { [key: string]: string } = {
      "Служебная": "Административно-управленческая (Служебная)",
      "Административно-управленческая": "Административно-управленческая (Служебная)",
      "Административно-управленческая (Служебная)": "Административно-управленческая (Служебная)",
      "Гостевая": "Гостевая",
      "Сценографическая": "Сценографическая",
      "Склад": "Склад"
    };
    
    return sectionMap[section] || section;
  };

  const getRoomsFilteredBySection = () => {
    if (!selectedSection || selectedSection === "Все секции") {
      return rooms;
    }
    
    const normalizedSelectedSection = normalizeSection(selectedSection);
    const filtered = rooms.filter(room => {
      const roomSection = room.section;
      
      if (normalizedSelectedSection === "Административно-управленческая (Служебная)") {
        return roomSection === "Административно-управленческая (Служебная)";
      }
      
      return roomSection === normalizedSelectedSection;
    });
    
    return filtered;
  };

  const matchesSearch = (room: any, searchText: string) => {
    if (!searchText) return true;
    
    const searchLower = searchText.toLowerCase();
    const roomNameLower = room.name.toLowerCase();
    const roomNumber = String(room.number);
    
    return roomNameLower.includes(searchLower) || 
           roomNumber.includes(searchText);
  };

  const displayRooms = getRoomsFilteredBySection();
  const filteredRooms = displayRooms.filter(room => matchesSearch(room, searchRoom));

  const formatRoomDisplay = (room: any) => {
    return `${room.name} (№${room.number})`;
  };

  // Показать все комнаты (сбросить фильтр комнаты)
  const handleShowAllRooms = () => {
    setSelectedFilters(prev => ({ ...prev, room: "" }));
    setSearchRoom("");
  };

  // Показать все категории (сбросить фильтр категории)
  const handleShowAllCategories = () => {
    setSelectedFilters(prev => ({ ...prev, category: "" }));
    setSearchCategory("");
  };

  // Функция для отображения названия с уточнением
  const getRoomDisplayName = (room: any) => {
    let suffix = "";
    if (room.number === "1") {
      if (room.building === "warehouse1") suffix = " (Склад 1)";
      else if (room.building === "warehouse2") suffix = " (Склад 2)";
      else if (room.building === "theatre") suffix = " (Театр)";
    }
    return `${room.name}${suffix}`;
  };

  return (
    <div className="poik__container">
      <div className="poisk">
        <ul className="filter-menu">
          {/* Сортировка */}
          <li className="filter-item">
            <button
              className={`filter-trigger ${openFilter === "sort" ? "active" : ""}`}
              onClick={() => toggleFilter("sort")}
            >
              Сортировка {sortType && "✓"}
            </button>
            <div
              className={`filter-content ${openFilter === "sort" ? "active" : ""}`}
            >
              <div className="sort-options">
                <a
                  href="#"
                  className={`sort-link ${sortType === "name-asc" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSortType("name-asc");
                  }}
                >
                  От А до Я
                </a>
                <a
                  href="#"
                  className={`sort-link ${sortType === "name-desc" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSortType("name-desc");
                  }}
                >
                  От Я до А
                </a>
                <a
                  href="#"
                  className={`sort-link ${sortType === "price-asc" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSortType("price-asc");
                  }}
                >
                  Сначала дешевые
                </a>
                <a
                  href="#"
                  className={`sort-link ${sortType === "price-desc" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSortType("price-desc");
                  }}
                >
                  Сначала дорогие
                </a>
              </div>
            </div>
          </li>

          {/* Стоимость */}
          <li className="filter-item">
            <button
              className={`filter-trigger ${openFilter === "price" ? "active" : ""}`}
              onClick={() => toggleFilter("price")}
            >
              Стоимость {(priceFrom !== "" || priceTo !== "") && "✓"}
            </button>
            <div
              className={`filter-content ${openFilter === "price" ? "active" : ""}`}
            >
              <div className="price-inputs">
                <input
                  type="number"
                  className="price-input"
                  placeholder="От"
                  value={priceFrom}
                  onChange={(e) =>
                    setPriceFrom(e.target.value ? Number(e.target.value) : "")
                  }
                />
                <input
                  type="number"
                  className="price-input"
                  placeholder="До"
                  value={priceTo}
                  onChange={(e) =>
                    setPriceTo(e.target.value ? Number(e.target.value) : "")
                  }
                />
              </div>
            </div>
          </li>

          {/* Категории */}
          <li className="filter-item">
            <button
              className={`filter-trigger ${openFilter === "categories" ? "active" : ""}`}
              onClick={() => toggleFilter("categories")}
            >
              Категории {selectedFilters.category && "✓"}
            </button>
            <div
              className={`filter-content ${openFilter === "categories" ? "active" : ""}`}
            >
              {selectedFilters.category && (
                <div className="filter-actions">
                  <button 
                    className="filter-show-all-btn"
                    onClick={handleShowAllCategories}
                  >
                    Все категории
                  </button>
                </div>
              )}
              
              <div className="filter-list">
                {uniqueCategories
                  .filter((cat) =>
                    cat.toLowerCase().includes(searchCategory.toLowerCase()),
                  )
                  .map((cat) => (
                    <div
                      key={cat}
                      className={`filter-list-item ${selectedFilters.category === cat ? "active" : ""}`}
                      onClick={() =>
                        setSelectedFilters((prev) => ({
                          ...prev,
                          category: prev.category === cat ? "" : cat,
                        }))
                      }
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedFilters.category === cat
                            ? "#e6f7ff"
                            : "transparent",
                      }}
                    >
                      {cat}
                    </div>
                  ))}
              </div>
            </div>
          </li>

          {/* Помещения */}
          <li className="filter-item">
            <button
              className={`filter-trigger ${openFilter === "rooms" ? "active" : ""}`}
              onClick={() => toggleFilter("rooms")}
            >
              Помещения {selectedFilters.room && "✓"}
              {selectedSection && selectedSection !== "Все секции" && (
                <span className="section-filter-badge"> ({selectedSection})</span>
              )}
            </button>
            <div
              className={`filter-content ${openFilter === "rooms" ? "active" : ""}`}
            >
              {selectedFilters.room && (
                <div className="filter-actions">
                  <button 
                    className="filter-show-all-btn"
                    onClick={handleShowAllRooms}
                  >
                    Все помещения
                  </button>
                </div>
              )}
              <input
                type="text"
                className="filter-input"
                placeholder="🔍 Поиск по названию или номеру..."
                value={searchRoom}
                onChange={(e) => setSearchRoom(e.target.value)}
              />
              
              {selectedSection && selectedSection !== "Все секции" && (
                <div className="filter-info">
                  Показаны комнаты секции: <strong>{selectedSection}</strong>
                  <span className="filter-info-count"> ({filteredRooms.length})</span>
                </div>
              )}
              
              <div className="filter-list">
                {filteredRooms.length > 0 ? (
                  filteredRooms.map((room) => (
                    <div
                      key={room.id}
                      className={`filter-list-item ${selectedFilters.room === room.id ? "active" : ""}`}
                      onClick={() => {
                        setSelectedFilters((prev) => ({
                          ...prev,
                          room: prev.room === room.id ? "" : room.id,
                        }));
                      }}
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedFilters.room === room.id
                            ? "#e6f7ff"
                            : "transparent",
                      }}
                    >
                      <div className="room-item">
                        <span className="room-name">
                          {getRoomDisplayName(room)}
                        </span>
                        <span className="room-number">(№{room.number})</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    {selectedSection && selectedSection !== "Все секции" 
                      ? `Нет комнат в секции "${selectedSection}"` 
                      : "Комнаты не найдены"}
                    {searchRoom && " по вашему запросу"}
                  </div>
                )}
              </div>
            </div>
          </li>
        </ul>

        <div className="search-container">
          <button className="newtype2" id="showAllBtn" onClick={resetFilters}>
            Сбросить все фильтры
          </button>
        </div>
      </div>
    </div>
  );
}

function getSectionColor(section: string): string {
  if (section.includes("Служебная")) return "#c8b4f0";
  if (section.includes("Гостевая")) return "#a0dcdc";
  if (section.includes("Сценографическая")) return "#aadcb4";
  if (section.includes("Склад")) return "#6496c8";
  return "#e0e0e0";
}